/**
 * Jobs Liked (Saved) store – GET jobs/like/ once per session per page.
 * Refetch after PATCH (like/unlike) from Saved or Gigs tab.
 */

import { create } from "zustand";
import apiClient from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";

export interface JobsApiJob {
  id: number;
  title: string;
  tbl: string;
  date: string;
  location: string;
  description: string;
  duration: number;
  audience: number;
  event_type: string;
  budget: string;
  is_applied: boolean;
  is_liked: boolean;
  pitched_artists: number;
  price: { original: number; discount: number; updated_price: number };
  status: boolean;
  type?: "Basic" | "Recommended" | "Express";
}

interface JobsLikedState {
  results: JobsApiJob[];
  count: number;
  lastFetchedPage: number | null;
  hasFetchedInSession: boolean;
  isLoading: boolean;
  error: string | null;

  fetchLiked: (page: number) => Promise<void>;
  ensureFetched: (page: number) => Promise<void>;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlightByPage: Record<number, Promise<void>> = {};

const initialState = {
  results: [],
  count: 0,
  lastFetchedPage: null as number | null,
  hasFetchedInSession: false,
  isLoading: false,
  error: null as string | null,
};

export const useJobsLikedStore = create<JobsLikedState>()((set, get) => ({
  ...initialState,

  fetchLiked: async (page: number) => {
    const existing = inFlightByPage[page];
    if (existing) return existing;
    const promise = (async () => {
      set({ isLoading: true, error: null });
      try {
        const url =
          page === 1
            ? API_ENDPOINTS.AUTH.JOBS_LIKED
            : `${API_ENDPOINTS.AUTH.JOBS_LIKED}?page=${page}`;
        const res = await apiClient.get<{
          success?: boolean;
          data?: { results?: JobsApiJob[]; count?: number };
        }>(url);
        const raw = res.data?.data ?? res.data;
        const data = raw as { results?: JobsApiJob[]; count?: number } | undefined;
        const results = data?.results ?? [];
        const count = data?.count ?? 0;
        set({
          results,
          count,
          lastFetchedPage: page,
          hasFetchedInSession: true,
          isLoading: false,
          error: null,
        });
      } catch {
        set({
          results: [],
          count: 0,
          isLoading: false,
          error: "Failed to load saved jobs",
        });
      } finally {
        delete inFlightByPage[page];
      }
    })();
    inFlightByPage[page] = promise;
    return promise;
  },

  ensureFetched: async (page: number) => {
    const { hasFetchedInSession, lastFetchedPage, fetchLiked } = get();
    if (hasFetchedInSession && lastFetchedPage === page) return;
    return fetchLiked(page);
  },

  setError: (error) => set({ error }),

  reset: () => {
    inFlightByPage = {};
    set(initialState);
  },
}));
